// ─── React & Libraries ───────────────────────────────────────
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

// ─── UI Components ──────────────────────────────────────────
import TextInput from "../../hook_forms/hook_textInput";
import PrimaryButton from "../../common/PrimaryButton";
import Spinner from "../../common/Spinner";
import { ToastContainer, toast } from "react-toastify";

// ─── API Services ───────────────────────────────────────────
import {
  addNewOrganization,
  addNewOrganizationUser,
  addOrganizationFeeTemplate,
} from "../../../setup/api/services/organization";

// ─── Styles ─────────────────────────────────────────────────
import styles from "./AddOrganisation.module.scss";

// ─────────────────── Zustand Store ───────────────────────────
import { useOrganisationStore } from "../../../providers/organisation/organisation.provider";
import { uploadFile } from "../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../s3Integration/enums";
import { getUserId } from "../../../setup/auth/oidc";
import PhoneRuleInput from "../../forms/phoneRuleInput";
import { getPhoneRuleConfigs } from "../../../setup/api/services/user";
import {
  FALLBACK_PHONE_RULES,
  PhoneRule,
  isValidFullNumber,
} from "../../../setup/util/phoneRules";
import { parseApiErrors } from "../../../setup/util/apiErrors";
import { IInviteResult } from "./StepThree";

// ─── Props ──────────────────────────────────────────────────
interface Props {
  onNext: (invite?: IInviteResult) => void;
}

// ─── StepTwo Component ──────────────────────────────────────
// Handles admin information and submits the organization form.
const StepTwo = ({ onNext }: Props) => {
  const { t } = useTranslation(); // ✅ For translations

  // React Hook Form methods
  const {
    register,
    watch,
    control,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [loading, setLoading] = useState(false);

  // ─── Zustand Selectors ─────────────────────────────────────
  const selectedOrganisation = useOrganisationStore(
    (state) => state.selectedOrganisation
  );
  const setSelectedOrganization = useOrganisationStore(
    (state) => state.setSelectedOrganization
  );
  const fetchOrganizations = useOrganisationStore(
    (state) => state.fetchOrganizations
  );

  // Watch values
  const adminFirstName = watch("adminFirstName");
  const adminLastName = watch("adminLastName");
  const email = watch("email");
  const mobile = watch("mobile");

  const userId = getUserId();

  const [rules, setRules] = useState<PhoneRule[]>(FALLBACK_PHONE_RULES);
  useEffect(() => {
    getPhoneRuleConfigs()
      .then((response) => {
        const enabled = (response?.data?.configs ?? []).filter(
          (config) => config.enabled
        );
        if (enabled.length > 0) setRules(enabled);
      })
      .catch(() => {
        // The service validates again; without the list Saudi numbers are offered.
      });
  }, []);

  const uploadImage = useCallback(
    (logo: File): Promise<string> => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        try {
          if (logo && userId) {
            const url = await uploadFile(
              logo,
              EUploadImageType.PROFILE,
              userId
            );
            resolve(url);
          } else {
            reject(new Error("Logo file is null"));
          }
        } catch (error) {
          reject(error);
        }
      });
    },
    [userId]
  );

  // ─── Submit Handler ───────────────────────────────────────
  const handleSubmitStepTwo = useCallback(async () => {
    const formValues = getValues();
    const {
      name,
      code,
      websiteUrl,
      logo,
      description,
      adminFirstName,
      adminLastName,
      email,
      mobile,
    } = formValues;

    setLoading(true);
    let logoUrl = "";

    if (typeof logo !== "string") {
      if (logo && logo.length > 0 && logo[0]) {
        try {
          logoUrl = await uploadImage(logo[0]);
        } catch (error) {
          logoUrl = "";
        }
      } else {
        logoUrl = "";
      }
    } else {
      logoUrl = logo;
    }
    const payload = {
      name,
      code,
      // fees,
      logoUrl: logoUrl || "",
      websiteUrl,
      description,
      mappedStatus: true,
      manualWithdrawEnabled: true,
    };

    try {
      const firstRes = await addNewOrganization(payload);
      const body = {
        email: email.trim(),
        firstName: adminFirstName.trim(),
        lastName: adminLastName.trim(),
        roles: ["CLIENT_ADMIN"],
        organizationCode: code,
        phoneNumber: mobile,
        clientUserId: mobile,
        clientId: firstRes?.data?.defaultPortalClientId,
      };
      const secondRequest = addNewOrganizationUser(body);

      const thirdRequest = addOrganizationFeeTemplate(firstRes?.data?.code, {
        name: `Default ${name} Fee Template`,
        description: `Default fee template for ${name}`,
        active: true,
        tiers: [
          {
            name: `Default ${name} tier`,
            from: 0,
          },
        ],
      });

      const [secondRes] = await Promise.all([secondRequest, thirdRequest]);

      onNext({
        userId: secondRes?.data?.userId,
        sent: !!secondRes?.data?.inviteEmailSent,
        existingAccount: !!secondRes?.data?.existingAccount,
      });
      toast(t("organization_saved_successfully"));
      fetchOrganizations();
    } catch (error) {
      console.error(error);
      // The service says why (e.g. the person belongs to another organization).
      toast(parseApiErrors(error, t("organization_save_error"))[0].message);
    } finally {
      setLoading(false);
      setSelectedOrganization();
    }
  }, [
    getValues,
    onNext,
    setSelectedOrganization,
    t,
    uploadImage,
    fetchOrganizations,
  ]);

  // No longer auto-submit StepTwo for Edit flow

  // ─── Validation Check ─────────────────────────────────────
  const isNextDisabled = useMemo(() => {
    return (
      !adminFirstName?.trim() ||
      !adminLastName?.trim() ||
      !email ||
      !email.includes("@") ||
      !isValidFullNumber(rules, mobile || "")
    );
  }, [adminFirstName, adminLastName, email, mobile, rules]);

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className={styles.step_container}>
      {/* Admin Name */}
      {!selectedOrganisation?.code && (
        <>
          <TextInput
            {...register("adminFirstName")}
            placeholder={t("lbl_first_name")}
            label={t("lbl_first_name")}
            errorMessage={errors.adminFirstName?.message?.toString()}
          />

          <TextInput
            {...register("adminLastName")}
            placeholder={t("lbl_last_name")}
            label={t("lbl_last_name")}
            errorMessage={errors.adminLastName?.message?.toString()}
          />

          {/* Email */}

          <TextInput
            {...register("email")}
            placeholder={t("email")}
            label={t("email")}
            errorMessage={errors.email?.message?.toString()}
          />

          {/* Mobile Number */}

          <Controller
            name="mobile"
            control={control}
            defaultValue=""
            render={({ field, fieldState }) => (
              <PhoneRuleInput
                rules={rules}
                value={field.value}
                onChange={field.onChange}
                placeholder={t("mobile_number")}
                countryLabel={t("lbl_country")}
                errorMessage={fieldState.error?.message}
                invalidMessage={t("err_invalid_phone_rules") as string}
              />
            )}
          />
        </>
      )}

      {/* Submit */}
      <div className={styles.button_container}>
        <PrimaryButton
          color="primary"
          onClick={handleSubmitStepTwo}
          disabled={loading || isNextDisabled}>
          {loading ? <Spinner /> : t("lbl_add")}
        </PrimaryButton>
      </div>

      <ToastContainer />
    </div>
  );
};

export default StepTwo;
